import { useState, useEffect, useCallback } from 'react';
import { tabStateManager, TabAuthData } from '@/utils/tabStateManager';
import { UserInfo, EncryptionKey } from '@/api/types/user';
import { logPrint } from '@/utils/my-utils';

/**
 * 标签页认证状态管理 Hook
 * 替代原来的 useTabAuth，提供浏览器多标签页独立认证状态管理
 */
export function useTabAuth() {
  const [authData, setAuthData] = useState<TabAuthData>(() => tabStateManager.getAuthData());
  const [isReady] = useState(true); // 浏览器环境始终就绪
  const [tabsCount, setTabsCount] = useState(1);

  // 同步认证数据状态
  const syncAuthData = useCallback(() => {
    const currentData = tabStateManager.getAuthData();
    setAuthData(currentData);
  }, []);

  // 监听其他标签页的认证状态变化
  useEffect(() => {
    // 监听认证状态变化
    const handleAuthChanged = (newAuthData: TabAuthData, fromTabId: string) => {
      console.log('TabAuth: 收到其他标签页认证状态变化:', fromTabId, newAuthData);
      // 可以选择是否同步其他标签页的认证状态
      // 这里选择不自动同步，保持标签页独立性
    };

    // 监听标签页消息
    const handleTabMessage = (data: any, fromTabId: string) => {
      logPrint('TabAuth: 收到其他标签页消息:', fromTabId, data);
      
      // 处理特定的消息类型
      if (data.type === 'logout_all') {
        // 全局登出：所有标签页都登出
        clearAuth();
      }
    };

    tabStateManager.onAuthChanged(handleAuthChanged);
    tabStateManager.onTabMessage(handleTabMessage);

    // 定期更新标签页数量
    const updateTabsCount = async () => {
      try {
        const count = await tabStateManager.getActiveTabsCount();
        setTabsCount(count);
      } catch (error) {
        console.warn('获取标签页数量失败:', error);
      }
    };

    updateTabsCount();
    const interval = setInterval(updateTabsCount, 10000); // 每10秒更新一次

    return () => {
      clearInterval(interval);
    };
  }, []);

  // 设置完整的认证数据
  const setAuth = useCallback((data: Partial<TabAuthData>) => {
    tabStateManager.setAuthData(data);
    syncAuthData();
  }, [syncAuthData]);

  // 清除所有认证信息
  const clearAuth = useCallback(() => {
    tabStateManager.clearAuth();
    syncAuthData();
  }, [syncAuthData]);

  // 设置访问令牌
  const setAccessToken = useCallback((token: string) => {
    tabStateManager.setAccessToken(token);
    syncAuthData();
  }, [syncAuthData]);

  // 设置用户ID
  const setUserId = useCallback((userId: string) => {
    tabStateManager.setUserId(userId);
    syncAuthData();
  }, [syncAuthData]);

  // 设置租户ID
  const setTenantId = useCallback((tenantId: string) => {
    tabStateManager.setTenantId(tenantId);
    syncAuthData();
  }, [syncAuthData]);

  // 设置组织代码
  const setOrganizationCode = useCallback((code: string) => {
    tabStateManager.setOrganizationCode(code);
    syncAuthData();
  }, [syncAuthData]);

  // 设置用户信息
  const setUserInfo = useCallback((userInfo: UserInfo) => {
    tabStateManager.setUserInfo(userInfo);
    syncAuthData();
  }, [syncAuthData]);

  // 设置加密密钥
  const setEncryptionKey = useCallback((encryptionKey: EncryptionKey) => {
    tabStateManager.setEncryptionKey(encryptionKey);
    syncAuthData();
  }, [syncAuthData]);

  // 批量设置登录数据
  const setLoginData = useCallback((data: {
    accessToken: string;
    userId: string;
    tenantId?: string;
    userInfo?: UserInfo;
    encryptionKey?: EncryptionKey;
  }) => {
    tabStateManager.setLoginData(data);
    syncAuthData();
  }, [syncAuthData]);

  // 检查是否已认证
  const isAuthenticated = useCallback(() => {
    return tabStateManager.isAuthenticated();
  }, []);

  // 获取访问令牌
  const getAccessToken = useCallback(() => {
    return tabStateManager.getAccessToken();
  }, []);

  // 获取用户ID
  const getUserId = useCallback(() => {
    return tabStateManager.getUserId();
  }, []);

  // 获取租户ID
  const getTenantId = useCallback(() => {
    return tabStateManager.getTenantId();
  }, []);

  // 获取组织代码
  const getOrganizationCode = useCallback(() => {
    return tabStateManager.getOrganizationCode();
  }, []);

  // 获取用户信息
  const getUserInfo = useCallback(() => {
    return tabStateManager.getUserInfo();
  }, []);

  // 获取加密密钥
  const getEncryptionKey = useCallback(() => {
    return tabStateManager.getEncryptionKey();
  }, []);

  // 获取标签页ID
  const getTabId = useCallback(() => {
    return tabStateManager.getTabId();
  }, []);

  // 向其他标签页广播消息
  const broadcastToOtherTabs = useCallback((message: any) => {
    tabStateManager.broadcastToOtherTabs(message);
  }, []);

  // 广播全局登出消息
  const broadcastLogoutAll = useCallback(() => {
    broadcastToOtherTabs({ type: 'logout_all' });
    clearAuth();
  }, [broadcastToOtherTabs, clearAuth]);

  // 刷新认证状态
  const refreshAuthState = useCallback(() => {
    syncAuthData();
  }, [syncAuthData]);

  return {
    // 状态数据
    authData,
    isReady,
    tabsCount,

    // 认证状态检查
    isAuthenticated,

    // 数据获取方法
    getAccessToken,
    getUserId,
    getTenantId,
    getOrganizationCode,
    getUserInfo,
    getEncryptionKey,
    getTabId,

    // 数据设置方法
    setAuth,
    setAccessToken,
    setUserId,
    setTenantId,
    setOrganizationCode,
    setUserInfo,
    setEncryptionKey,
    setLoginData,

    // 认证管理
    clearAuth,
    refreshAuthState,

    // 标签页通信
    broadcastToOtherTabs,
    broadcastLogoutAll,

    // 调试工具
  };
}
